export type Column<T> = {
	label: string,
	align?: 'left' | 'right',
	buildString: ( row: T ) => string,
}

export function repeat( char: string, count: number ) {
	return new Array( count + 1 ).join( char )
}

export function buildTableData<T>( columns: Column<T>[], rows: T[] ) {
	const maxWidths: number[] = []
	const headerRow: string[] = []

	columns.forEach( ( col ) => {
		const label = col.label

		headerRow.push( label )
		maxWidths.push( label.length )
	} )

	const dataRows = rows.map( ( row ) => {
		const dataRow: string[] = []

		columns.forEach( ( col, colIndex ) => {
			const data = col.buildString( row )
			dataRow.push( data )

			if ( data.length > maxWidths[ colIndex ] ) {
				maxWidths[ colIndex ] = data.length
			}
		} )

		return dataRow
	} )

	return { maxWidths, headerRow, dataRows }
}

export function formatCell( value: string, width: number, align: 'left' | 'right' = 'left' ) {
	const widthDiff = width - value.length
	let paddedValue = value

	if ( widthDiff ) {
		const paddingString = repeat( ' ', widthDiff )

		if ( align === 'right' ) {
			paddedValue = paddingString + value
		} else {
			paddedValue = value + paddingString
		}
	}

	return `| ${paddedValue} `
}

export function buildRowString<T>( dataRow: string[], maxWidths: number[], columns: Column<T>[], isHeaderRow = false ) {
	let rowString = ''

	dataRow.forEach( ( dataCell, colIndex ) => {
		rowString += formatCell(
			dataCell,
			maxWidths[ colIndex ],
			isHeaderRow ? undefined : columns[ colIndex ].align // header row is default aligned
		)
	} )

	rowString += '|'

	if ( isHeaderRow ) {
		const borderWidth = rowString.length
		rowString = rowString.concat( '\r\n', repeat( '-', borderWidth ) )
	}

	return rowString += '\r\n'
}

/**
 * Formats the given data set as a string table
 * Table cells are delimited by pipe characters, each cell padded by a single space on each side,
 * columns are each sized to fit the widest data cell in that column
 *
 * @typedef {Object} Column
 * @property {function} buildString Takes a row object, return's the cell string
 * @property {string} label column header
 * @property {string} align (optional) "right" or "left", defaults "left"
 *
 * @param {Column[]} columns column definitions for the data set
 * @param {Object[]} rows the data set
 */
export default function formatTable<T>( columns: Column<T>[], rows: T[] ) {
	const { maxWidths, headerRow, dataRows } = buildTableData( columns, rows )

	let stringTable = buildRowString( headerRow, maxWidths, columns, true )
	dataRows.forEach( ( dataRow ) => {
		stringTable += buildRowString( dataRow, maxWidths, columns )
	} )

	return stringTable
}
